// @flow
import type { Node } from 'react';
import {
  REQUEST_ACCESSORIES,
  RECEIVE_ACCESSORIES,
  REQUEST_ACCESSORY,
  RECEIVE_ACCESSORY,
  ADD_ACCESSORY_TO_CART,
  ADDED_ACCESSORY_TO_CART,
  ACCESSORY_QUANTITY_CHANGE,
  ACCESSORY_VARIANT_CHANGE,
  REQUEST_ACCESSORY_PRICE,
  RECEIVE_ACCESSORY_PRICE,
} from '../../ActionsConst';
import { showToast, createToastId, buildAndShowErrorToast } from '../../core/actions/toast/ToastActions';
import toastMsg from '../../../data/toast';
import { getIdFromRoute } from '../../data/AccessoriesNavItems';
import { cartUpdate } from '../../core/actions/cart/CartActions';
import { showOverlay, closeOverlay } from '../../core/actions/overlay/OverlayActions';
import AnalyticsClient from '../../../clients/AnalyticsClient';
import accessoriesEvents from '../../../analytics/events/accessories';
import AccessoriesServiceClient from '../../../clients/AccessoriesService/AccessoriesServiceClient';

const cartStatus = {
  OK: 'OK',
  ERROR: 'ERROR',
  NOT_ENOUGH_STOCK: 'NOT_ENOUGH_STOCK',
};

export const requestAccessories = (activeTab: string) => ({
  type: REQUEST_ACCESSORIES,
  activeTab,
});

export const receiveAccessories = (accessories: Object) => ({
  type: RECEIVE_ACCESSORIES,
  accessories,
});

export const requestAccessory = () => ({
  type: REQUEST_ACCESSORY,
});

export const requestAccessoryPrice = () => ({
  type: REQUEST_ACCESSORY_PRICE,
});

export const receiveAccessoryPrice = (data: Object) => ({
  type: RECEIVE_ACCESSORY_PRICE,
  price: data.price,
});

export const receiveAccessory = (accessory: Object) => ({
  type: RECEIVE_ACCESSORY,
  accessory: accessory.items[0],
});

export const addAccessoryToCart = () => ({
  type: ADD_ACCESSORY_TO_CART,
});

export const addedAccessoryToCart = (isAdded: boolean) => ({
  type: ADDED_ACCESSORY_TO_CART,
  isAdded,
});

export const quantityChange = (quantity: number) => ({
  type: ACCESSORY_QUANTITY_CHANGE,
  quantity,
});

export const variantChange = (criteria: Array<Object>) => ({
  type: ACCESSORY_VARIANT_CHANGE,
  criteria,
});

const addToCartError = (error) => {
  const msg =
    error === cartStatus.NOT_ENOUGH_STOCK
      ? toastMsg.accessoriesAddToCartNotEnoughStockFailure
      : toastMsg.accessoriesAddToCartFailure;
  return showToast({
    error: true,
    id: createToastId(),
    message: {
      messageDescriptor: msg,
    },
  });
};

const addToCartSuccess = () =>
  showToast({
    id: createToastId(),
    message: {
      messageDescriptor: toastMsg.accessoriesAddToCartSuccess,
    },
  });

const getAccessories = (activeTab: string, fromItem: number) => {
  return (dispatch: Function, getState: Function, baseApiUrl: string) => {
    dispatch(requestAccessories(activeTab));

    const category = activeTab !== 'all-moo-products' ? getIdFromRoute(activeTab) : '';
    return AccessoriesServiceClient.getAccessories(baseApiUrl, fromItem, category)
      .then((data) => {
        dispatch(receiveAccessories(data));
      })
      .catch(() => {
        dispatch(receiveAccessories({ items: [], total: 0 }));
        dispatch(buildAndShowErrorToast(toastMsg.generalErrorReceivedMessage));
      });
  };
};

const getAccessory = (productType: string) => (dispatch: Function, getState: Function, baseApiUrl: string) => {
  dispatch(requestAccessory());
  return AccessoriesServiceClient.getAccessory(baseApiUrl, productType).then((data) => {
    dispatch(receiveAccessory(data));
  });
};

const updateAccessoryPrice = () => (dispatch: Function, getState: Function, apiUrl: string) => {
  if (getState().accessories.activeItem.selectedVariant) {
    const productName = getState().accessories.activeItem.selectedVariant.name;
    const quantity = getState().accessories.activeItem.cartQuantity;
    if (productName && quantity) {
      dispatch(requestAccessoryPrice());
      return AccessoriesServiceClient.getAccessoryPrice(apiUrl, productName, quantity).then((data) => {
        dispatch(receiveAccessoryPrice(data));
      });
    }
  }
  return null;
};

const accessoryCartRequest = (productName: string, cartQuantity: number) => {
  return (dispatch: Function, getState: Function, baseApiUrl: string) => {
    dispatch(addAccessoryToCart());
    return AccessoriesServiceClient.addToCart(
      baseApiUrl,
      productName,
      cartQuantity,
      getState().config.selectedStoreCode,
    )
      .then((data) => {
        if (data.status === cartStatus.OK) {
          dispatch(addedAccessoryToCart(true));
          dispatch(addToCartSuccess());
          dispatch(cartUpdate(data.count));
          AnalyticsClient.trackEvent(accessoriesEvents.accessoryAddToCart(productName, cartQuantity));
        } else {
          dispatch(addToCartError(data.status));
          dispatch(addedAccessoryToCart(false));
        }
      })
      .catch(() => {
        dispatch(addToCartError(cartStatus.ERROR));
        dispatch(addedAccessoryToCart(false));
      });
  };
};

const accessoryQuantityChange = (quantity: number) => {
  return (dispatch: Function) => {
    dispatch(quantityChange(quantity));
    dispatch(updateAccessoryPrice());
  };
};

const accessoryVariantChange = (criteria: Array<Object>) => {
  return (dispatch: Function) => {
    dispatch(variantChange(criteria));
    dispatch(updateAccessoryPrice());
  };
};

const openCustomiseRequestForm = ({ content }: { content: Node }) => {
  return (dispatch: Function) => {
    dispatch(
      showOverlay({
        overlayName: 'customise_request_form',
        overlayContent: content,
      }),
    );
  };
};

const closeCustomiseRequestForm = () => {
  return (dispatch: Function) => {
    dispatch(closeOverlay());
  };
};

const accessoriesActions = {
  getAccessories,
  getAccessory,
  accessoryCartRequest,
  addedAccessoryToCart,
  accessoryQuantityChange,
  accessoryVariantChange,
  openCustomiseRequestForm,
  closeCustomiseRequestForm,
};

export default accessoriesActions;
